import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { QueueItem } from "../lib/types";

const STORAGE_KEY = "orion1.phase1.draft";

export type DraftState = {
  target_company: string;
  time_fence: string;
  ctpp_active: boolean;
  search_engines: string[];
  openrouter_model_cheap: string;
  openrouter_model_pro: string;
  phase2_extraction_model: string;
  phase3_synthesis_model: string;
  resolved_prompt_text: string;
  prompt_raw: string;
  queue_items: QueueItem[];
};

const DEFAULT_DRAFT: DraftState = {
  target_company: "",
  time_fence: "",
  ctpp_active: true,
  search_engines: ["brave_keyword"],
  openrouter_model_cheap: "",
  openrouter_model_pro: "",
  phase2_extraction_model: "",
  phase3_synthesis_model: "",
  resolved_prompt_text: "",
  prompt_raw: "",
  queue_items: [],
};

type DraftContextValue = {
  draft: DraftState;
  setDraft: (patch: Partial<DraftState>) => void;
  setQueueItems: (items: QueueItem[]) => void;
  resetDraft: () => void;
};

const DraftStateContext = createContext<DraftContextValue | null>(null);

function loadStoredDraft(): DraftState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_DRAFT };
    const parsed = JSON.parse(raw) as Partial<DraftState>;
    return { ...DEFAULT_DRAFT, ...parsed };
  } catch {
    return { ...DEFAULT_DRAFT };
  }
}

export function DraftStateProvider({ children }: { children: ReactNode }) {
  const [draft, setDraftState] = useState<DraftState>(loadStoredDraft);

  useEffect(() => {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(draft));
  }, [draft]);

  const setDraft = useCallback((patch: Partial<DraftState>) => {
    setDraftState((prev) => ({ ...prev, ...patch }));
  }, []);

  const setQueueItems = useCallback((items: QueueItem[]) => {
    setDraftState((prev) => ({ ...prev, queue_items: items }));
  }, []);

  const resetDraft = useCallback(() => {
    setDraftState({ ...DEFAULT_DRAFT });
  }, []);

  const value = useMemo(
    () => ({ draft, setDraft, setQueueItems, resetDraft }),
    [draft, setDraft, setQueueItems, resetDraft],
  );

  return (
    <DraftStateContext.Provider value={value}>{children}</DraftStateContext.Provider>
  );
}

export function useDraftState(): DraftContextValue {
  const ctx = useContext(DraftStateContext);
  if (!ctx) {
    throw new Error("useDraftState must be used within DraftStateProvider");
  }
  return ctx;
}

export function draftHudPayload(draft: DraftState): Record<string, unknown> {
  return {
    target_company: draft.target_company,
    time_fence: draft.time_fence,
    ctpp_active: draft.ctpp_active,
    search_engines: draft.search_engines,
    openrouter_model_cheap: draft.openrouter_model_cheap,
    openrouter_model_pro: draft.openrouter_model_pro,
    phase2_extraction_model: draft.phase2_extraction_model,
    phase3_synthesis_model: draft.phase3_synthesis_model,
    resolved_prompt_text: draft.resolved_prompt_text,
  };
}

export function draftExecutePayload(
  draft: DraftState,
  prefs: { phase3_enable_pdr_reasoning: boolean; pdr_schema_profile: string },
): Record<string, unknown> {
  const approved = draft.queue_items.filter((i) => i.selected !== false);
  return {
    ...draftHudPayload(draft),
    approved_queue_items: approved,
    phase3_enable_pdr_reasoning: prefs.phase3_enable_pdr_reasoning,
    pdr_schema_profile: prefs.pdr_schema_profile,
  };
}
